import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import ServicesSection from '../elements/services-1';
import ChooseUsSection from '../elements/choose-us';
import Modal from '../elements/modal';
import AvailableCitiesModal from '../elements/available-cities-modal';
import { ServiceTrustBlock } from '../elements/premium';
import axios from 'axios';
import { getLocalItem, getSessionItem } from '../../utils/storage';

// Vehicle Data API
import { getBrands, getModels, getBikeServices, getZomatoPricing } from '../../utils/vehicleApi';

// (brand,model) -> cc_range bucket. Dormant until `enabled` is true (owner
// spot-check, Approval Register). Unknown/ambiguous models => manual fallback.
import bikeModelCc from '../../data/bike_model_cc.json';

const ccNorm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
const resolveBikeCc = (brandObj, modelObj) => {
  if (!bikeModelCc || !bikeModelCc.enabled || !brandObj || !modelObj) return null;
  const bKeys = [brandObj.label, brandObj.value].map(ccNorm).filter(Boolean);
  const mKeys = [modelObj.label, modelObj.value].map(ccNorm).filter(Boolean);
  for (const b of bKeys) {
    const models = bikeModelCc.map && bikeModelCc.map[b];
    if (!models) continue;
    for (const m of mKeys) {
      if (models[m]) return models[m];
    }
  }
  return null;
};

const AC_TIERS = [
  { label: 'Hatchback', examples: 'Wagon R, Swift, i10, Alto', vehicleName: 'Hatchback', price: '\u20B91,999', rawPrice: 1999 },
  { label: 'Sedan / SUV', examples: 'City, Verna, Creta, Seltos', vehicleName: 'Sedan / SUV', price: '\u20B92,399', rawPrice: 2399, highlight: true },
  { label: 'Luxury', examples: 'Audi, BMW, Mercedes, Jeep', vehicleName: 'Luxury Car', price: '\u20B912,999', rawPrice: 12999 },
];

function AcRepairSection({ brand, model }) {
  return (
    <section style={{ background: '#fafafa', padding: '48px 0', borderTop: '1px solid #eee' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <p style={{ color: '#f42222', fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
            Also Available
          </p>
          <h3 style={{ fontSize: 'clamp(1.3rem,3vw,1.9rem)', fontWeight: 800, color: '#0d0d0d', margin: '0 0 8px' }}>
            Car AC Repair Service
          </h3>
          <p style={{ color: '#666', fontSize: 15, margin: 0 }}>
            Doorstep AC service by certified mechanics &mdash; 12-point check, 30-day warranty
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 20, maxWidth: 860, margin: '0 auto' }}>
          {AC_TIERS.map((tier) => (
            <div
              key={tier.label}
              style={{
                background: tier.highlight ? '#f42222' : '#fff',
                border: tier.highlight ? 'none' : '1px solid #e8e8e8',
                borderRadius: 16,
                padding: '28px 24px',
                textAlign: 'center',
                boxShadow: tier.highlight ? '0 8px 28px rgba(244,34,34,0.35)' : '0 2px 12px rgba(0,0,0,0.05)',
                transform: tier.highlight ? 'scale(1.04)' : 'none',
                position: 'relative',
              }}
            >
              {tier.highlight && (
                <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: '#fff', color: '#f42222', fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                  Most Popular
                </div>
              )}
              <div style={{ fontWeight: 700, fontSize: 17, color: tier.highlight ? '#fff' : '#0d0d0d', marginBottom: 4 }}>{tier.label}</div>
              <div style={{ color: tier.highlight ? 'rgba(255,255,255,0.7)' : '#999', fontSize: 12, marginBottom: 16 }}>{tier.examples}</div>
              <div style={{ fontSize: 36, fontWeight: 900, color: tier.highlight ? '#fff' : '#f42222', marginBottom: 4 }}>{tier.price}</div>
              <div style={{ color: tier.highlight ? 'rgba(255,255,255,0.6)' : '#aaa', fontSize: 11, marginBottom: 20 }}>Service charge · Gas refill billed separately</div>
              <Link
                to={{
                  pathname: '/checkout',
                  state: {
                    vehicle_category: 'car',
                    vehicle_name: tier.vehicleName,
                    service_type: 'AC Repair',
                    price: tier.rawPrice,
                    brand: brand ? brand.label : '',
                    fuel_type: '',
                    transmission: '',
                    cc_range: '',
                  },
                }}
                style={{
                  display: 'block',
                  background: tier.highlight ? '#fff' : '#f42222',
                  color: tier.highlight ? '#f42222' : '#fff',
                  padding: '11px 0',
                  borderRadius: 30,
                  fontWeight: 800,
                  fontSize: 14,
                  textDecoration: 'none',
                }}
              >
                Book AC Repair
              </Link>
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', color: '#aaa', fontSize: 12, marginTop: 20 }}>
          <Link to="/car-ac-repair-near-me" style={{ color: '#f42222', textDecoration: 'underline' }}>View full AC repair details &rarr;</Link>
        </p>
      </div>
    </section>
  );
}

class Services extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileApp: false,
      vehicleType: 'bike',
      isElectric: false,
      brand: '',
      model: '',
      fuelType: '',
      transmission: 'manual',
      ccRange: '',
      brandDropdownOpen: false,
      modelDropdownOpen: false,
      fuelTypeDropdownOpen: false,
      transmissionDropdownOpen: false,
      ccRangeDropdownOpen: false,
      isMouseInsideDropdown: false,
      // API-fetched brand lists
      carBrandsData: [],
      bikeBrandsData: [],
      evBrandsData: [],
      // API-fetched model lists
      carModels: [],
      bikeModels: [],
      evModels: [],
      // API-fetched services
      currentServices: null,
      servicesLoading: false,
      // User vehicles
      userVehicles: [],
      selectedVehicleIndex: 0,
      // Rider check
      isZomatoRider: false,
    };
  }

  // Fetch all brands from API
  fetchBrandsFromAPI = async () => {
    try {
      const [carRes, bikeRes, evRes] = await Promise.all([
        getBrands('car'),
        getBrands('bike'),
        getBrands('ev'),
      ]);
      this.setState({
        carBrandsData: carRes.brands || [],
        bikeBrandsData: bikeRes.brands || [],
        evBrandsData: evRes.brands || [],
      });
    } catch (error) {
      // Silently handle — brands won't show
    }
  };

  // Fetch models from API when brand is selected
  fetchModelsFromAPI = async (brandValue, type) => {
    try {
      const res = await getModels(brandValue, type);
      const models = (res.models || []).slice().sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

      if (type === 'ev') {
        this.setState({ evModels: models });
      } else if (type === 'car') {
        this.setState({ carModels: models });
      } else {
        this.setState({ bikeModels: models });
      }
    } catch (error) {
      // Silently handle
    }
  };

  // Fetch services from API
  fetchServicesFromAPI = async () => {
    const { vehicleType, isElectric, brand, model, fuelType, ccRange, isZomatoRider } = this.state;

    if (!brand || !model) return;

    this.setState({ servicesLoading: true, currentServices: null });

    try {
      if (vehicleType === 'car' && fuelType) {
        // Car services from API
        const API_BASE = process.env.REACT_APP_API_BASE || 'https://www.ridenrepair.com/api';
        const res = await axios.get(`${API_BASE}/v2/vehicle-data/services`, {
          params: {
            brand: brand.value || brand.label,
            model: model.value || model.label,
            fuel_type: fuelType,
            type: 'car',
          },
        });

        // Flatten categories → sub_categories → services into a flat array
        const services = [];
        if (res.data && res.data.categories) {
          res.data.categories.forEach((cat) => {
            (cat.sub_categories || []).forEach((subCat) => {
              (subCat.services || []).forEach((svc) => {
                services.push(this.normalizeService(svc));
              });
            });
          });
        }
        this.setState({ currentServices: services, servicesLoading: false });
      } else if (vehicleType === 'bike' && !isElectric && ccRange) {
        // Bike services from API
        let res;
        if (isZomatoRider) {
          res = await getZomatoPricing(ccRange);
        } else {
          res = await getBikeServices(ccRange);
        }
        const services = (res.services || []).map((svc) => this.normalizeService(svc));
        this.setState({ currentServices: services, servicesLoading: false });
      } else if (vehicleType === 'bike' && isElectric) {
        // EV services — flat-rate pricing from bike_services with cc_range='ev'
        const res = await getBikeServices('ev');
        const services = (res.services || []).map((svc) => this.normalizeService(svc));
        this.setState({ currentServices: services, servicesLoading: false });
      } else {
        this.setState({ servicesLoading: false });
      }
    } catch (error) {
      this.setState({ currentServices: null, servicesLoading: false });
    }
  };

  // Normalize API response keys (underscore → hyphen) for ServicesSection compatibility
  normalizeService = (svc) => ({
    id: svc.id,
    label: svc.label,
    value: svc.value,
    price: svc.price,
    'strikethrough-price': svc.strikethrough_price || svc['strikethrough-price'],
    'time-taken': svc.time_taken || svc['time-taken'],
    perks: svc.perks || [],
    checklist: svc.checklist || [],
    'image-icon': svc.image_icon || svc['image-icon'],
  });

  handleBrandChange = (brand) => {
    const { vehicleType, isElectric } = this.state;
    const type = isElectric ? 'ev' : vehicleType;
    this.setState({ model: '', currentServices: null, fuelType: '' });
    this.fetchModelsFromAPI(brand.value || brand.label, type);
  };

  handleClickDropdown = (dropdownName) => {
    Object.keys(this.state).forEach((key) => {
      if (key.endsWith('DropdownOpen') && key !== `${dropdownName}DropdownOpen`) {
        this.setState({ [key]: false });
      }
    });
    this.setState((prevState) => ({
      [`${dropdownName}DropdownOpen`]: !prevState[`${dropdownName}DropdownOpen`],
    }));
  };

  handleDropdownOptionsMouseEnter = () => {
    this.isMouseInsideDropdown = true;
  };

  handleDropdownOptionsMouseLeave = () => {
    this.isMouseInsideDropdown = false;
  };

  handleMouseEnter = (dropdownName) => {
    if (dropdownName === 'brand' && this.state.brand) return;
    if (dropdownName === 'model' && this.state.model) return;
    Object.keys(this.state).forEach((key) => {
      if (key.endsWith('DropdownOpen') && key !== `${dropdownName}DropdownOpen`) {
        this.setState({ [key]: false });
      }
    });
    this.setState({ [`${dropdownName}DropdownOpen`]: true });
  };

  handleMouseLeave = (dropdownName) => {
    setTimeout(() => {
      if (!this.isMouseInsideDropdown) {
        this.setState({ [`${dropdownName}DropdownOpen`]: false });
      }
    }, 200);
  };

  handleFuelTypeChange = (type) => {
    this.setState({ fuelType: type }, () => {
      this.fetchServicesFromAPI();
    });
  };

  handleCCRangeChange = (type) => {
    this.setState({ ccRange: type }, () => {
      this.fetchServicesFromAPI();
    });
  };

  handleTransmissionChange = (type) => {
    this.setState({ transmission: type });
  };

  handleVehicleTypeChange = (type) => {
    this.setState({
      vehicleType: type,
      isElectric: false,
      brand: '',
      model: '',
      fuelType: '',
      transmission: 'manual',
      ccRange: '',
      currentServices: null,
    });
    this.closeDropdowns();
  };

  handleElectricTypeChange = (isElectric) => {
    this.setState({
      isElectric,
      brand: '',
      model: '',
      fuelType: '',
      ccRange: '',
      currentServices: null,
    });
    this.closeDropdowns();
  };

  renderDropdown = (options, selectedOption, type, prefix) => {
    const tryRequireLogo = (logo) => {
      try {
        return require(`../../images/brand_logos/${logo}`);
      } catch (e) {
        return null;
      }
    };

    return (
      <div className="vehicle-dropdown">
        <div
          className="dropdown-label"
          onMouseEnter={() => this.handleMouseEnter(prefix)}
          onMouseLeave={() => this.handleMouseLeave(prefix)}
          onClick={() => this.handleClickDropdown(prefix)}
          ref={(ref) => (this.dropdownRef = ref)}
        >
          {selectedOption && (
            <div className="selected-option">
              {prefix === 'brand' && selectedOption.logo && tryRequireLogo(selectedOption.logo) && (
                <img src={tryRequireLogo(selectedOption.logo)} alt={`${selectedOption.label} brand logo`} className="brand-image selected-image" />
              )}
              <span>{selectedOption.label}</span>
            </div>
          )}
          {!selectedOption && `Select ${type}`}
          <div className="arrow-down"></div>
        </div>
        {this.state[`${prefix}DropdownOpen`] && (
          <div className="dropdown-options" onMouseEnter={this.handleDropdownOptionsMouseEnter} onMouseLeave={this.handleDropdownOptionsMouseLeave}>
            {options.map((option) => (
              <div
                className="option-item"
                key={option.id || option.label}
                onClick={() => {
                  this.setState({ [prefix]: option });
                  this.handleClickDropdown(prefix);
                  if (prefix === 'brand') {
                    this.handleBrandChange(option);
                  }
                  if (prefix === 'model') {
                    // Auto-select first fuel type if available
                    if (option.fuelTypes && option.fuelTypes.length > 0) {
                      this.setState({ fuelType: option.fuelTypes[0].value }, () => {
                        this.fetchServicesFromAPI();
                      });
                    } else if (this.state.vehicleType === 'car') {
                      // Default to petrol for cars
                      this.setState({ fuelType: 'petrol' }, () => {
                        this.fetchServicesFromAPI();
                      });
                    } else if (this.state.isElectric) {
                      // EV — fetch immediately
                      this.setState({ model: option }, () => {
                        this.fetchServicesFromAPI();
                      });
                    } else if (this.state.vehicleType === 'bike') {
                      // Non-electric bike: try to auto-derive the CC bucket so
                      // the manual CC step can be skipped. Unknown/ambiguous
                      // model OR guard disabled => leave ccRange empty so the
                      // existing manual CC buttons show (zero regression). The
                      // price is keyed only on cc_range, so a resolved value is
                      // byte-identical to the same value picked manually.
                      const cc = resolveBikeCc(this.state.brand, option);
                      if (cc) {
                        this.setState({ ccRange: cc }, () => {
                          this.fetchServicesFromAPI();
                        });
                      }
                    }
                  }
                }}
              >
                <div className="brand-option">
                  {prefix === 'brand' && option.logo && tryRequireLogo(option.logo) && (
                    <img src={tryRequireLogo(option.logo)} alt={`${option.label} brand logo`} className="brand-image" />
                  )}
                  {option.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  closeDropdowns = () => {
    Object.keys(this.state).forEach((key) => {
      if (key.endsWith('DropdownOpen')) {
        this.setState({ [key]: false });
      }
    });
  };

  handleDocumentClick = (event) => {
    let targetElement = event.target;
    while (targetElement) {
      if (targetElement.classList && targetElement.classList.contains('dropdown-label')) return;
      targetElement = targetElement.parentElement;
    }
    this.closeDropdowns();
  };

  checkRider = (phone) => {
    return axios
      .get('https://ridenrepair.com/auto/check_rider', { params: { phone_number: phone } })
      .then((response) => response.data.exists)
      .catch(() => false);
  };

  findBrandData = (brandName, vehicleType, isElectric) => {
    const { carBrandsData, bikeBrandsData, evBrandsData } = this.state;
    if (isElectric) return evBrandsData.find((b) => b.label === brandName);
    const brands = vehicleType === 'car' ? carBrandsData : bikeBrandsData;
    return brands.find((b) => b.label === brandName);
  };

  setSelectedVehicle = () => {
    const { userVehicles, selectedVehicleIndex } = this.state;
    const vehicle = userVehicles[selectedVehicleIndex];
    if (!vehicle || vehicle === '') {
      this.setState({
        vehicleType: 'bike',
        brand: '',
        model: '',
        fuelType: '',
        transmission: 'manual',
        ccRange: '',
        currentServices: null,
      });
    } else {
      this.setState({ vehicleType: vehicle.vehicleType });
      const brandData = this.findBrandData(vehicle.brand, vehicle.vehicleType);
      if (brandData) {
        this.setState({ brand: brandData });
        // Fetch models for this brand
        const type = vehicle.vehicleType;
        this.fetchModelsFromAPI(brandData.value || brandData.label, type).then(() => {
          // After models are fetched, find and set the model
          const stateKey = type === 'car' ? 'carModels' : type === 'ev' ? 'evModels' : 'bikeModels';
          const models = this.state[stateKey];
          const modelData = models.find((m) => m.label === vehicle.model);
          if (modelData) {
            this.setState({ model: modelData });
          }
        });
      }
      if (vehicle.vehicleType === 'car') {
        this.setState({ fuelType: vehicle.fuelType, transmission: vehicle.transmission });
      } else if (vehicle.vehicleType === 'bike') {
        this.setState({ ccRange: vehicle.ccRange });
      }
    }
  };

  handleVehicleChange = (event) => {
    const selectedVehicleIndex = event.target.value;
    if (selectedVehicleIndex === '') {
      this.setState({
        selectedVehicleIndex: '',
        vehicleType: 'bike',
        brand: '',
        model: '',
        fuelType: '',
        transmission: 'manual',
        ccRange: '',
        currentServices: null,
      });
    } else {
      this.setState({ selectedVehicleIndex }, () => {
        this.setSelectedVehicle();
      });
    }
  };

  customSortByPopularity = (brandArray, top10List) => {
    return [...brandArray].sort((a, b) => {
      const aIndex = top10List.indexOf(a.label);
      const bIndex = top10List.indexOf(b.label);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.label.localeCompare(b.label);
    });
  };

  async componentDidMount() {
    // Fetch brands from API
    this.fetchBrandsFromAPI();

    // Fetch user vehicles
    axios
      .get('https://ridenrepair.com/api/get_user_vehicles', { params: { phone: this.props.user.ph } })
      .then((response) => {
        if (response.status === 200 && response.data.vehicles.length > 0) {
          this.setState({ userVehicles: response.data.vehicles, selectedVehicleIndex: 0 }, () => {
            this.setSelectedVehicle();
          });
        }
      })
      .catch(() => {});

    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleDocumentClick);
    }
    if (typeof window !== 'undefined') {
      if (getLocalItem('phone_provided') !== 'yes' && getSessionItem('phone_ignored') !== 'yes') {
        this.props.setOpenModal(true);
      }
    }

    // Check if Zomato rider
    const isZomatoRider = await this.checkRider(this.props.user.ph);
    if (isZomatoRider) {
      this.setState({ isZomatoRider: true });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  render() {
    const {
      vehicleType, isElectric, brand, model, fuelType, transmission, ccRange,
      bikeModels, carModels, evModels, userVehicles, selectedVehicleIndex,
      carBrandsData, bikeBrandsData, evBrandsData, currentServices, servicesLoading,
    } = this.state;
    const { isMobileBrowser, isMobileApp, citiesModal, setCitiesModal, openModal, setOpenModal, user, setUser } = this.props;

    const canonicalUrl = 'https://www.ridenrepair.com/service';

    const top10CarBrands = ['Maruti Suzuki', 'Hyundai', 'Tata', 'Mahindra', 'Kia', 'Toyota', 'Honda', 'Renault', 'Volkswagen', 'Skoda'];
    const top10BikeBrands = ['Hero', 'Honda', 'TVS', 'Bajaj', 'Royal Enfield', 'Yamaha', 'Suzuki', 'KTM', 'Jawa', 'BMW'];

    const showCarServices = vehicleType === 'car' && brand && model && fuelType && currentServices;
    const showBikeServices = vehicleType === 'bike' && !isElectric && brand && model && ccRange && currentServices;
    const showEvServices = vehicleType === 'bike' && isElectric && brand && model && currentServices;

    return (
      <div className="rr-service rr-service--lux">
        <Helmet>
          <link rel="canonical" href={canonicalUrl} />
          <title>Service Checklist - Ride N Repair</title>
          <meta name="description" content="Choose your vehicle and explore our range of doorstep bike and car services. Transparent pricing, certified mechanics, 30-day warranty." />
        </Helmet>

        {citiesModal && <AvailableCitiesModal isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} closeModal={() => setCitiesModal(false)} />}
        {!isMobileApp && openModal && <Modal isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} openModal={openModal} setOpenModal={setOpenModal} user={user} setUser={setUser} />}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        {/* Above-the-fold price/trust hero ONLY — the vehicle picker now follows
            immediately below it (picker-as-hero IA). The heavy trust content
            (why-us / steps / FAQ) moves under the results via section="details". */}
        <ServiceTrustBlock section="hero" />

        <div className="page-content bg-white">
          <div className="page-banner ovbl-dark vehicle-services-header" style={{ backgroundColor: 'black', overflow: 'visible' }}>
            <div className="container">
              <div className="page-banner-entry text-center">
                <h1><span>Services</span></h1>
                <nav aria-label="breadcrumb" className="breadcrumb-row">
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/"><i className="las la-home"></i>Home</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Services</li>
                  </ul>
                </nav>

                <div>
                  {userVehicles.length > 0 ? (
                    <div className="vehicle-dropdown">
                      <select value={selectedVehicleIndex} onChange={this.handleVehicleChange}>
                        <option value="">Select your vehicle</option>
                        {userVehicles.map((vehicle, index) => (
                          <option key={index} value={index}>{vehicle.model}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <h3 style={{ marginTop: '3%', color: 'white' }}>Choose Your Vehicle</h3>
                  )}
                </div>

                <div id="vehicle-picker" className="vehicle-selection-row">
                  <div className="vertical-dropdown-container">
                    <div className="vehicle-type-buttons specific-vehicle-type">
                      <button className={`vehicle-type-button ${vehicleType === 'car' ? 'active' : ''}`} onClick={() => this.handleVehicleTypeChange('car')}>Car</button>
                      <button className={`vehicle-type-button ${vehicleType === 'bike' ? 'active' : ''}`} onClick={() => this.handleVehicleTypeChange('bike')}>Bike</button>
                    </div>

                    {vehicleType === 'bike' && (
                      <div className="vehicle-type-buttons">
                        <button className={`vehicle-type-button ${!isElectric ? 'active' : ''}`} onClick={() => this.handleElectricTypeChange(false)}>Non-Electric</button>
                        <button className={`vehicle-type-button ${isElectric ? 'active' : ''}`} onClick={() => this.handleElectricTypeChange(true)}>Electric</button>
                      </div>
                    )}

                    <div className="vehicle-dropdowns">
                      {/* CAR BRAND DROPDOWN */}
                      {vehicleType === 'car' && this.renderDropdown(
                        this.customSortByPopularity(carBrandsData, top10CarBrands),
                        brand, 'Brand', 'brand'
                      )}

                      {/* CAR MODEL DROPDOWN */}
                      {vehicleType === 'car' && brand && this.renderDropdown(carModels, model, 'Model', 'model')}

                      {/* CAR FUEL TYPE BUTTONS */}
                      {vehicleType === 'car' && model && (
                        <div className="vehicle-type-buttons">
                          {(model.fuelTypes || [{ id: 1, label: 'Petrol', value: 'petrol' }, { id: 2, label: 'Diesel', value: 'diesel' }]).map((ft) => (
                            <button
                              key={ft.id || ft.value}
                              className={`vehicle-type-button ${fuelType === ft.value ? 'active' : ''}`}
                              onClick={() => this.handleFuelTypeChange(ft.value)}
                            >
                              {ft.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* CAR TRANSMISSION BUTTONS */}
                      {vehicleType === 'car' && (
                        <div className="vehicle-type-buttons">
                          <button className={`vehicle-type-button ${transmission === 'manual' ? 'active' : ''}`} onClick={() => this.handleTransmissionChange('manual')}>Manual</button>
                          <button className={`vehicle-type-button ${transmission === 'automatic' ? 'active' : ''}`} onClick={() => this.handleTransmissionChange('automatic')}>Automatic</button>
                        </div>
                      )}

                      {/* BIKE BRAND DROPDOWN */}
                      {vehicleType === 'bike' && !isElectric && this.renderDropdown(
                        this.customSortByPopularity(bikeBrandsData, top10BikeBrands),
                        brand, 'Brand', 'brand'
                      )}

                      {/* BIKE MODEL DROPDOWN */}
                      {vehicleType === 'bike' && !isElectric && brand && this.renderDropdown(bikeModels, model, 'Model', 'model')}

                      {/* BIKE CC RANGE BUTTONS */}
                      {vehicleType === 'bike' && !isElectric && brand && model && (
                        <div className="vehicle-type-buttons cc-range-buttons">
                          <div className="cc-range-button-group">
                            <button className={`vehicle-type-button cc-range-button ${ccRange === '0-199cc' ? 'active' : ''}`} onClick={() => this.handleCCRangeChange('0-199cc')}>0 to 199 cc</button>
                            <button className={`vehicle-type-button cc-range-button ${ccRange === '200-249cc' ? 'active' : ''}`} onClick={() => this.handleCCRangeChange('200-249cc')}>200 to 249 cc</button>
                            <button className={`vehicle-type-button cc-range-button ${ccRange === '250-400cc' ? 'active' : ''}`} onClick={() => this.handleCCRangeChange('250-400cc')}>250 to 400 cc</button>
                          </div>
                          <div className="cc-range-button-group">
                            <button className={`vehicle-type-button cc-range-button ${ccRange === '401-500cc' ? 'active' : ''}`} onClick={() => this.handleCCRangeChange('401-500cc')}>401 to 500 cc</button>
                            <button className={`vehicle-type-button cc-range-button ${ccRange === '501-800cc' ? 'active' : ''}`} onClick={() => this.handleCCRangeChange('501-800cc')}>501 to 800 cc</button>
                            <button className={`vehicle-type-button cc-range-button ${ccRange === '801cc+' ? 'active' : ''}`} onClick={() => this.handleCCRangeChange('801cc+')}>801 cc and above</button>
                          </div>
                        </div>
                      )}

                      {/* EV BRAND DROPDOWN */}
                      {vehicleType === 'bike' && isElectric && this.renderDropdown(
                        [...evBrandsData].sort((a, b) => a.label.localeCompare(b.label)),
                        brand, 'Brand', 'brand'
                      )}

                      {/* EV MODEL DROPDOWN */}
                      {vehicleType === 'bike' && isElectric && brand && this.renderDropdown(evModels, model, 'Model', 'model')}
                    </div>
                  </div>

                  {/* Dynamic vehicle image */}
                  {model && model.imagePath && (
                    <div className="vehicle-image-container">
                      <img
                        src={(() => {
                          try { return require(`../../images/vehicle_images/${model.imagePath}`); }
                          catch (e) { return null; }
                        })()}
                        alt={`${vehicleType} ${model.label}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Loading indicator */}
          {servicesLoading && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <p style={{ color: '#666', fontSize: 16 }}>Loading services...</p>
            </div>
          )}

          {/* Car services */}
          {showCarServices && (
            <ServicesSection
              isMobileBrowser={isMobileBrowser}
              isMobileApp={isMobileApp}
              vehicleType={vehicleType}
              model={model}
              brand={brand}
              fuelType={fuelType}
              transmission={transmission}
              ccRange={ccRange}
              servicesData={currentServices}
              user={user}
              setUser={setUser}
            />
          )}

          {/* AC Repair cross-sell for cars */}
          {vehicleType === 'car' && <AcRepairSection brand={brand} model={model} />}

          {/* Bike services */}
          {showBikeServices && (
            <ServicesSection
              isMobileBrowser={isMobileBrowser}
              isMobileApp={isMobileApp}
              vehicleType={vehicleType}
              model={model}
              brand={brand}
              fuelType={fuelType}
              transmission={transmission}
              ccRange={ccRange}
              servicesData={currentServices}
              user={user}
              setUser={setUser}
            />
          )}

          {/* EV services */}
          {showEvServices && (
            <ServicesSection
              isMobileBrowser={isMobileBrowser}
              isMobileApp={isMobileApp}
              vehicleType="bike"
              model={model}
              brand={brand}
              fuelType={fuelType}
              transmission={transmission}
              ccRange="200-249cc"
              servicesData={currentServices}
              user={user}
              setUser={setUser}
            />
          )}

          {/* Trust details (why-us grid → doorstep promise → FAQ) — moved BELOW the
              picker + results so the primary action is near the top of the page. */}
          <ServiceTrustBlock section="details" />

          <ChooseUsSection isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} />
        </div>

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={this.props.setCitiesModal} />
      </div>
    );
  }
}

export default Services;
